import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  Input,
  ViewChild
} from '@angular/core';
import { Subject } from 'rxjs';

import { NgClass } from '@angular/common';
import { ValueAccessorProviderFactory } from '../../value-accessor/value-accessor-provider-factory';
import { BaseValueAccessor } from '../../value-accessor/base-value-accessor';
import { YearPickerComponent } from '../year-picker/year-picker.component';
import { MonthPickerComponent } from '../month-picker/month-picker.component';
import { DayPickerComponent } from '../day-picker/day-picker.component';
import { DatePickerInnerComponent } from '../date-picker-inner/date-picker-inner.component';
import { OverlayComponent } from '../../overlay/overlay.component';
import { OverlayService } from '../../overlay/overlay.service';
import { IdService } from '../../../services/id.service';
import { RangeSelectorCalendarConfig } from './range-selector-calendar.config';

import type { DatePickerMode } from '../date-picker/date-picker-mode.enum';
import type { DateValidator } from '../date-picker/date-validator.type';
import type {
  CalendarStyleType,
  DateRange,
  DateRangeKeys,
  RangeDateChange
} from '../date-range-selector.domain';
import type { Subscription } from 'rxjs';
import type {
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  TemplateRef
} from '@angular/core';
import type { DatePickerFormatType } from '../../../services/date/date-formatter.service';
import type { RangeSelectorCalendarInputDirective } from './range-selector-calendar-input.directive';

/**
 * The Range Selector Calendar Component
 *
 * This is the input for selecting range of dates
 */
@Component({
  selector: 'td-wb-range-selector-calendar',
  templateUrl: './range-selector-calendar.component.html',
  styleUrls: ['./range-selector-calendar.component.scss'],
  providers: [
    ValueAccessorProviderFactory(RangeSelectorCalendarComponent)
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [OverlayComponent, NgClass, DatePickerInnerComponent, DayPickerComponent, MonthPickerComponent, YearPickerComponent]
})
export class RangeSelectorCalendarComponent extends BaseValueAccessor<DateRange> implements OnInit, OnDestroy, OnChanges {
  private static readonly RANGE_PICKER_ID_PREFIX: string = 'td-wb-range-date-picker';

  @ViewChild('rangePickerTemplate', { static: true }) content: TemplateRef<unknown>;

  @ViewChild('dateTemplate', { static: true }) dateContent: TemplateRef<unknown>;

  @Input() calendarStyle: CalendarStyleType = 'regular';

  /**
   * Unique id set for each datepicker
   */
  uniqueId: Array<string> = [];

  /**
   * Format of day in month
   */
  formatDay: DatePickerFormatType;

  /**
   * Format of month in year.
   */
  formatMonth: DatePickerFormatType;

  /**
   * Format of year in year range.
   */
  formatYear: DatePickerFormatType;

  /**
   * Format of day in week header.
   */
  formatDayHeader: DatePickerFormatType;

  /**
   * Format of title when selecting day.
   */
  formatDayTitle: DatePickerFormatType;

  /**
   * Format of title when selecting month.
   */
  formatMonthTitle: DatePickerFormatType;

  /**
   * Starting day of the week from 0-6 (0=Sunday, ..., 6=Saturday)
   */
  startingDay: number;

  /**
   * Number of years to be displayed in the year selection
   */
  yearRange: number;

  /**
   * Sets datepicker mode, supports: day, month, year; defaults to day
   */
  mode: DatePickerMode;

  /**
   * Sets minimum datepicker mode, supports: day, month, year; defaults to day
   */
  minMode: DatePickerMode;

  /**
   * Sets maximum datepicker mode, supports: day, month, year; defaults to year
   */
  maxMode: DatePickerMode;

  /**
   * if false week numbers will be hidden; defaults to false
   */
  showWeeks: boolean;

  /**
   * Function which should return true if date in the selector should be disabled for selection.
   */
  disabledDates: DateValidator;

  startDateParsed: Date;

  endDateParsed: Date;

  isEndDateActive: boolean = false;
  isStartDateActive: boolean = false;

  readonly selectedChanged: Subject<DateRange> = new Subject<DateRange>();

  private dateRangePickerInputs: Map<DateRangeKeys, RangeSelectorCalendarInputDirective>
    = new Map<DateRangeKeys, RangeSelectorCalendarInputDirective>();

  private inputSubscriptions: Array<Subscription> = new Array<Subscription>();

  private _value: DateRange = { startDate: undefined, endDate: undefined };
  public get value(): DateRange {
    return this._value;
  }

  public set value(value: DateRange) {
    this._value = value;
  }

  /**
   * The constructor of the component.
   * Sets configuration options
   *
   * @param config The default configuration options
   * @param overlayService
   * @param idService
   */
  constructor(@Inject(RangeSelectorCalendarConfig) private config: RangeSelectorCalendarConfig,
              @Inject(OverlayService) private overlayService: OverlayService,
              @Inject(IdService) private idService: IdService) {
    super();
    this.setConfigureOptions();
  }

  /**
   * This function sets validators, unique id, and overlank link label
   */
  ngOnInit(): void {
    this.uniqueId[0] = this.idService.generateNamespaceSequentialId(RangeSelectorCalendarComponent.RANGE_PICKER_ID_PREFIX);
    this.uniqueId[1] = this.idService.generateNamespaceSequentialId(RangeSelectorCalendarComponent.RANGE_PICKER_ID_PREFIX);
  }

  ngOnDestroy(): void {
    this.inputSubscriptions.forEach((s: Subscription): void => s.unsubscribe());
    this.selectedChanged.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.calendarStyle !== undefined) {
      if (changes.calendarStyle.currentValue === 'regular') {
        this.isStartDateActive = false;
        this.isEndDateActive = false;
      }
      if (changes.calendarStyle.currentValue === 'compact') {
        if (!this.isEndDateActive && !this.isStartDateActive) {
          this.isStartDateActive = true;
          this.isEndDateActive = false;
        }
      }
    }
  }

  getFormat(key: DateRangeKeys): DatePickerFormatType {
    return this.dateRangePickerInputs[key] === undefined ? null : this.dateRangePickerInputs[key].format;
  }

  getMinDate(key: DateRangeKeys): Date | null {
    return this.dateRangePickerInputs[key] === undefined ? null : this.dateRangePickerInputs[key].minDate;
  }

  getMaxDate(key: DateRangeKeys): Date | null {
    return this.dateRangePickerInputs[key] === undefined ? null : this.dateRangePickerInputs[key].maxDate;
  }

  /**
   * Updates fromDate and toDate
   *
   * @param change Changed ngModel value
   */
  updateValue(change: RangeDateChange): void {
    if (change !== null && change !== undefined) {
      this.onPickerChange(change.value, change.field);
    }
  }

  /**
   * This function is called when date is selected from date picker component
   *
   * @param date Date selected from date picker component
   * @param input Type of date picker
   */
  onPickerChange(date: Date, input: 'startDate' | 'endDate'): void {
    const parsed: string = input + 'Parsed';
    this[parsed] = date;
    this.onValueChange();
  }

  close(): void {
    this.overlayService.close();
  }

  /**
   * Sets default configuration options
   */
  setConfigureOptions(): void {
    Object.assign(this, this.config);
  }

  /**
   * Returns  isDisabledDate validator
   *
   * @returns isDisabledDate validator
   */
  disabledDateInput(key: DateRangeKeys): DateValidator {
    return (date: Date): boolean => this.dateRangePickerInputs[key]?.isDisabledDate(date) ?? true;
  }

  registerInput(input: RangeSelectorCalendarInputDirective, key: DateRangeKeys): void {
    this.dateRangePickerInputs[key] = input;
    const inputSubscription: Subscription =
      input.valueChange
        .subscribe((change: RangeDateChange | null): void => {
          this.updateValue(change);
        });
    this.inputSubscriptions.push(inputSubscription);
  }

  setActiveCalendar(key: DateRangeKeys): void {
    this.isStartDateActive = key === 'startDate';
    this.isEndDateActive = !this.isStartDateActive;
  }

  getActiveCalendarKey(): string {
    if (this.isStartDateActive) {
      return 'startDate';
    }
    if (this.isEndDateActive) {
      return 'endDate';
    }
  }

  private onValueChange(): void {
    const customRange: {
      startDate: Date,
      endDate: Date
    } = {
      startDate: this.startDateParsed,
      endDate: this.endDateParsed
    };
    this.value = customRange;
    this.selectedChanged.next(this.value);
  }
}
